import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import WaveLoader from '../components/WaveLoader';

function AgriAudio({ filters, loading, setLoading, isStaging, onDrilldown, currentUser }) {
  const isSuperAdmin = currentUser?.organization === 'Dachido' && currentUser?.username === 'admin_dachido';

  const [subTab, setSubTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [processed, setProcessed] = useState([]);
  const [pending, setPending] = useState([]);
  const [failed, setFailed] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedTranscription, setSelectedTranscription] = useState(null);
  const [transcriptionData, setTranscriptionData] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');

  // Super-admin only: fetch the org list to drive the org picker
  useEffect(() => {
    if (!isSuperAdmin) return;
    axios.get('/api/config/organizations')
      .then(res => {
        const orgs = (res.data.data || []).filter(o => o.type !== 'admin');
        setOrganizations(orgs);
        if (orgs.length && !selectedOrg) setSelectedOrg(orgs[0].name);
      })
      .catch(() => {});
  }, [isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSuperAdmin && !selectedOrg) return; // wait for org picker to initialize

    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.append(k, v);
    });
    if (isSuperAdmin && selectedOrg) params.append('organization', selectedOrg);

    const requests = [
      axios.get(`/api/agri/audio/overview?${params}`),
    ];

    if (subTab === 'processed') requests.push(axios.get(`/api/agri/audio/processed?${params}`));
    if (subTab === 'pending') requests.push(axios.get(`/api/agri/audio/pending?${params}`));
    if (subTab === 'failed') requests.push(axios.get(`/api/agri/audio/failed?${params}`));
    if (subTab === 'languages') requests.push(axios.get(`/api/agri/audio/language-breakdown?${params}`));
    if (subTab === 'analytics') requests.push(axios.get(`/api/agri/audio/analytics?${params}`));

    Promise.all(requests)
      .then(([ovRes, ...rest]) => {
        setOverview(ovRes.data);
        if (subTab === 'processed' && rest[0]) setProcessed(rest[0].data || []);
        if (subTab === 'pending' && rest[0]) setPending(rest[0].data || []);
        if (subTab === 'failed' && rest[0]) setFailed(rest[0].data || []);
        if (subTab === 'languages' && rest[0]) setLanguages(rest[0].data?.data || rest[0].data || []);
        if (subTab === 'analytics' && rest[0]) setAnalytics(rest[0].data);
      })
      .catch(err => console.error('Error loading Agri Audio:', err))
      .finally(() => setLoading(false));
  }, [filters, subTab, setLoading, isSuperAdmin, selectedOrg]);

  const loadTranscription = (filename) => {
    setSelectedTranscription(filename);
    // Processed recordings now come straight from the DB with the transcript
    // already attached — no separate detail request needed.
    const rec = processed.find(r => r.filename === filename);
    setTranscriptionData(rec ? { duration: formatDuration(rec.audio_duration), transcription: rec.transcription } : null);
  };

  const retryFailed = (filename) => {
    const params = new URLSearchParams();
    if (isSuperAdmin && selectedOrg) params.append('organization', selectedOrg);
    axios.post(`/api/agri/audio/retry/${encodeURIComponent(filename)}?${params}`)
      .then(() => {
        setFailed(prev => prev.filter(f => f.filename !== filename));
      })
      .catch(err => console.error('Error retrying:', err));
  };

  const cardStyle = {
    background: '#fff', borderRadius: 8, padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };
  const plotLayout = { margin: { t: 30, b: 40, l: 50, r: 20 }, height: 300, paper_bgcolor: 'transparent', plot_bgcolor: 'transparent' };
  const colors = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
  const thStyle = { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #ddd', fontWeight: 600 };
  const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #eee' };

  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const s = Math.round(seconds);
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
  };

  const subTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'processed', label: 'Processed' },
    { id: 'pending', label: 'Pending' },
    { id: 'failed', label: 'Failed' },
    { id: 'languages', label: 'Languages' },
    { id: 'analytics', label: 'Analytics' },
  ];

  return (
    <div>
      {/* Org picker (Dachido super-admin only) */}
      {isSuperAdmin && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
          <label style={{ fontWeight: 600, color: '#495057', fontSize: 13 }}>Organization:</label>
          <select
            value={selectedOrg}
            onChange={e => setSelectedOrg(e.target.value)}
            style={{ padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #dee2e6', minWidth: 160 }}
          >
            {organizations.map(o => <option key={o.name} value={o.name}>{o.display_name}</option>)}
          </select>
        </div>
      )}

      {/* Sub-tab navigation */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid #eee', paddingBottom: 0 }}>
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: subTab === tab.id ? '#3498db' : 'transparent',
              color: subTab === tab.id ? '#fff' : '#666',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontWeight: subTab === tab.id ? 600 : 400,
              fontSize: 13,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {subTab === 'overview' && loading && (
        <div className="loading"><WaveLoader size="large" /></div>
      )}
      {subTab === 'overview' && !loading && overview && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div style={cardStyle}>
            <div style={{ fontSize: 12, color: '#666' }}>Total Recordings</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{overview.total || 0}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: 12, color: '#666' }}>Processed</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#2ecc71' }}>{overview.processed || 0}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: 12, color: '#666' }}>Pending</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#f39c12' }}>{overview.pending || 0}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: 12, color: '#666' }}>Failed</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#e74c3c' }}>{overview.failed || 0}</div>
          </div>
        </div>
      )}
      {subTab === 'overview' && !loading && !overview && (
        <div style={{ ...cardStyle, textAlign: 'center', color: '#999', padding: 40 }}>No overview data available</div>
      )}

      {/* Processed Recordings */}
      {subTab === 'processed' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>Processed Recordings</h3>
          {loading ? (
            <div className="loading"><WaveLoader size="large" /></div>
          ) : processed.length > 0 ? (
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Filename</th>
                    <th style={thStyle}>Duration</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {processed.map((r, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{r.filename}</td>
                      <td style={tdStyle}>{formatDuration(r.audio_duration)}</td>
                      <td style={tdStyle}>{r.upload_timestamp ? new Date(r.upload_timestamp).toLocaleDateString() : 'N/A'}</td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => loadTranscription(r.filename)}
                          style={{ background: '#3498db', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No processed recordings</div>
          )}
        </div>
      )}

      {/* Pending Recordings */}
      {subTab === 'pending' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>Pending Recordings</h3>
          {loading ? (
            <div className="loading"><WaveLoader size="large" /></div>
          ) : pending.length > 0 ? (
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Filename</th>
                    <th style={thStyle}>Size</th>
                    <th style={thStyle}>Duration</th>
                    <th style={thStyle}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((r, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{r.filename}</td>
                      <td style={tdStyle}>{r.size ? `${(r.size / 1024 / 1024).toFixed(1)} MB` : 'N/A'}</td>
                      <td style={tdStyle}>{formatDuration(r.audio_duration)}</td>
                      <td style={tdStyle}>{r.upload_timestamp ? new Date(r.upload_timestamp).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No pending recordings</div>
          )}
        </div>
      )}

      {/* Failed Recordings */}
      {subTab === 'failed' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>Failed Recordings</h3>
          {loading ? (
            <div className="loading"><WaveLoader size="large" /></div>
          ) : failed.length > 0 ? (
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Filename</th>
                    <th style={thStyle}>Size</th>
                    <th style={thStyle}>Error</th>
                    <th style={thStyle}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {failed.map((r, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{r.filename}</td>
                      <td style={tdStyle}>{r.size ? `${(r.size / 1024 / 1024).toFixed(1)} MB` : 'N/A'}</td>
                      <td style={tdStyle}>{r.error || r.stage || 'Unknown'}</td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => retryFailed(r.filename)}
                          style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                        >
                          Retry
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No failed recordings</div>
          )}
        </div>
      )}

      {/* Language Breakdown */}
      {subTab === 'languages' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>Language Breakdown</h3>
          {loading ? (
            <div className="loading"><WaveLoader size="large" /></div>
          ) : languages.length > 0 ? (
            <Plot
              data={[{
                labels: languages.map(l => l.language),
                values: languages.map(l => l.count),
                type: 'pie',
                hole: 0.4,
                marker: { colors },
              }]}
              layout={{ ...plotLayout, showlegend: true }}
              config={{ responsive: true, displayModeBar: false }}
              style={{ width: '100%' }}
            />
          ) : (
            <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No language data</div>
          )}
        </div>
      )}

      {/* Analytics */}
      {subTab === 'analytics' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>Processing Analytics</h3>
          {loading ? (
            <div className="loading"><WaveLoader size="large" /></div>
          ) : analytics ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Total Processed</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#2ecc71' }}>{analytics.total_processed || 0}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Total Failed</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#e74c3c' }}>{analytics.total_failed || 0}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Success Rate</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#2ecc71' }}>
                  {analytics.success_rate != null ? `${analytics.success_rate.toFixed(1)}%` : 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Avg Processing Time</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {analytics.avg_processing_time != null ? `${analytics.avg_processing_time.toFixed(1)}s` : 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Avg Audio Duration</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {analytics.avg_audio_duration != null ? `${analytics.avg_audio_duration.toFixed(1)}s` : 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Quality Score</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {analytics.quality_score != null ? `${analytics.quality_score.toFixed(1)}%` : 'N/A'}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No analytics data</div>
          )}
        </div>
      )}

      {/* Transcription Modal */}
      {selectedTranscription && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
          background: '#fff', boxShadow: '-2px 0 8px rgba(0,0,0,0.15)',
          zIndex: 1000, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: 14 }}>Transcription: {selectedTranscription}</h3>
            <button
              onClick={() => { setSelectedTranscription(null); setTranscriptionData(null); }}
              style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}
            >
              x
            </button>
          </div>
          <div style={{ flex: 1, padding: 20, overflowY: 'auto' }}>
            {transcriptionData ? (
              <div>
                {transcriptionData.language && (
                  <div style={{ marginBottom: 12 }}>
                    <strong>Language:</strong> {transcriptionData.language}
                  </div>
                )}
                {transcriptionData.duration && (
                  <div style={{ marginBottom: 12 }}>
                    <strong>Duration:</strong> {transcriptionData.duration}
                  </div>
                )}
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6 }}>
                  {transcriptionData.text || transcriptionData.transcription || 'No transcription available'}
                </div>
              </div>
            ) : (
              <WaveLoader />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AgriAudio;
